import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { RepoMarkdown } from '../../components/markdown/RepoMarkdown';
import { ProductDesignPageHeader, ProductDesignSection } from './ProductDesignDocPrimitives';
import { DeprecatedBanner } from '../../components/DeprecatedBanner';
import {
  DESIGN_SKILLS,
  SKILL_REPO_BASE,
  SKILL_ROUTE_BASE,
  loadSkillFile,
  skillFiles,
  splitFrontMatter,
} from './designSkills';

/**
 * One agent skill, shown in the Design Language area. The text is the file in
 * .claude/skills, loaded at build time — this page adds nothing to it.
 */
export const PrioritiesAISkillPage: React.FC = () => {
  const params = useParams();
  const skillName = params.skill ?? '';
  const file = params['*'] || 'SKILL.md';
  const skill = DESIGN_SKILLS.find((s) => s.name === skillName);
  const [text, setText] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let live = true;
    setText(null);
    setMissing(false);
    const p = skill ? loadSkillFile(skill.name, file) : null;
    if (!p) {
      setMissing(true);
      return;
    }
    p.then((t) => {
      if (live) setText(t);
    });
    return () => {
      live = false;
    };
  }, [skill, file]);

  if (!skill || missing) {
    return (
      <ProductDesignPageHeader
        title="Skill not found"
        eyebrow="Agent skills"
        subtitle={<>No such skill file. The skills are listed on the design language page, section 07.</>}
      />
    );
  }

  const files = skillFiles(skill.name);
  const isMain = file === 'SKILL.md';
  const { description, body } = isMain && text ? splitFrontMatter(text) : { description: null, body: text ?? '' };
  const base = `${SKILL_ROUTE_BASE}/${skill.name}`;
  const dir = file.includes('/') ? file.slice(0, file.lastIndexOf('/') + 1) : '';

  /** Links between skill files stay inside the Design Language area. */
  const hrefFor = (href: string | undefined): { to?: string; href?: string } => {
    if (!href) return {};
    if (/^(https?:|mailto:|#|\/)/.test(href)) return { href };
    const target = (dir + href).replace(/^\.\//, '').split('#')[0];
    return files.includes(target) ? { to: target === 'SKILL.md' ? base : `${base}/${target}` } : { href };
  };

  return (
    <>
      {skill.deprecated && (
        <DeprecatedBanner
          to={`${SKILL_ROUTE_BASE}/priorities-ai-design-system`}
          label="priorities-ai-design-system"
          still="It describes how older screens were built."
        />
      )}
      <ProductDesignPageHeader
        title={skill.name}
        eyebrow={skill.deprecated ? 'Deprecated · agent skill' : 'Agent skill'}
        subtitle={
          <>
            {isMain ? (description ?? skill.def) : <>Reference file: <code className="font-mono text-[0.9em]">{file}</code></>}
          </>
        }
      />

      <ProductDesignSection
        title="Files in this skill"
        hint={`The source is .claude/skills/${skill.name}/ in the repository. This page shows the file as it is.`}
      >
        <div className="flex flex-wrap gap-2.5">
          {files.map((f) => (
            <Link
              key={f}
              to={f === 'SKILL.md' ? base : `${base}/${f}`}
              className={`border px-3 py-1.5 font-mono text-[13px] transition-colors ${
                f === file
                  ? 'bg-[var(--surface-raised)] text-[var(--text-pri)]'
                  : 'text-[var(--text-sec)] hover:text-[var(--text-pri)]'
              }`}
              style={{ borderColor: 'var(--rule-bright)' }}
            >
              {f}
            </Link>
          ))}
          <a
            href={`${SKILL_REPO_BASE}/${skill.name}/${file}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 font-mono text-[13px] text-[var(--p-gold)] underline-offset-4 hover:underline"
          >
            Open in the repository →
          </a>
        </div>
      </ProductDesignSection>

      <ProductDesignSection title={file}>
        {text === null ? (
          <p className="text-sm text-[var(--text-ter)]">Loading…</p>
        ) : (
          <RepoMarkdown resolveLink={hrefFor}>{body}</RepoMarkdown>
        )}
      </ProductDesignSection>
    </>
  );
};
